/*
 * Confidential and Proprietary.
 * Do not distribute without Bloom Harbor Gifts consent.
 * Copyright Bloom Harbor Gifts 2019. All rights reserved.
 */
/* eslint-disable react/no-array-index-key */
import React from 'react';
import {
    object, func, number, arrayOf, string, bool, shape,
} from 'prop-types';
import { withStyles } from '@material-ui/core/styles';
import ListItem from '@material-ui/core/ListItem';
import ListItemText from '@material-ui/core/ListItemText';
import Typography from '@material-ui/core/Typography';
import ReactHtmlParser from 'react-html-parser';
import Grid from '@material-ui/core/Grid';
import { useSelector } from 'react-redux';
import { getFeatureFlag } from '../../../../../../../../state/ducks/App/ducks/Config/Config-Selectors';

const styles = (theme) => ({
    wine: {
        width: '450px',
        margin: '15px',
    },
    addonNames: {
        fontSize: '18px',
        fontWeight: '400',
        fontFamily: theme.typography.fontFamily,
        color: theme.palette.white,
    },
    menuName: {
        color: theme.palette.colorIconHeader,
    },
    listItem: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
    },
    listData: {
        display: 'flex',
        alignItems: 'center',
    },
    imageWine: {
        maxWidth: '75%',
    },
    addons: {
        fontSize: '16px',
        fontWeight: '400',
        fontFamily: theme.typography.fontFamily,
    },
    addonsMobile: {
        fontSize: '13px',
        fontWeight: '400',
        fontFamily: theme.typography.fontFamily,
    },
    buttonControl: {
        display: 'flex',
        justifyContent: 'space-between',
    },
    button: {
        borderRadius: '50%',
        border: `1px solid ${theme.palette.black}`,
        fontSize: 22,
        padding: 0,
        background: 'transparent',
        fontWeight: 400,
        margin: '0px 5px 0px 5px',
        width: 32,
        height: 32,
        outline: 'none',
        color: theme.palette.select || '#646464',
        '&:hover': {
            cursor: 'pointer',
            background: theme.palette.select || '#646464',
            color: theme.palette.white,
        },
    },
    inputlabel: {
        color: theme.palette.white,
        fontSize: '20px',
        fontWeight: '400',
        fontFamily: theme.typography.fontFamily,
        padding: '0px 0px 0px 8px',
    },
    addonsMobileSection: {
        '& > .MuiListItem-gutters': {
            paddingLeft: 0,
            paddingRight: 0,
        },
    },
});

/*
* @description return the sale price of add on item
* @param {object} item
* @param {string} type
*/
const getPrice = (item, type = 'sale') => {
    let priceList = (item?.prices || []).filter((price) => price.type === type) || [];
    if (priceList.length > 0) {
        priceList = priceList[0];
        return priceList?.value;
    }
    return 0;
};

const AddonItemsList =  (
    {
        selectedAddons,
        handleAddonChange,
        drawer,
        wineValidation,
        maxAllowedAddons,
        classes,
        options,
        hover,
        setLimitError,
        isMobile,
    },
) => {
    const wineModelOnAction = useSelector(getFeatureFlag('is-inline-wine-verification-model-on-atc-enabled')) || false;

    /*
    * @description get availability based on age and zipcode
    */
    const getAvailability = () => {
        let availability = false;
        if (!wineValidation) {
            return false;
        }
        const keys = Object.keys(wineValidation?.wineAvailability || {});
        keys.forEach((key) => {
            if (wineValidation?.wineAvailability?.[key]?.isProductAvailable) {
                availability = true;
            }
        });
        return availability;
    };

    const buildImgPath = (path, name) => `${path + name}x.jpg`;

    const getShortDescription = (shortDesc) => shortDesc;

    /*
    * @description increment the item from selection list
    * @param {object} item
    */
    const handleIncrement = (item) => {
        const totalSelectedAddons = ((selectedAddons || []).map((addon) => addon.qty) || [0]).reduce((a, b) => a + b, 0);
        if (totalSelectedAddons < maxAllowedAddons) {
            if (item.ageVerifyFlag && !getAvailability() && !isMobile && !wineModelOnAction) {
                drawer(true, item);
            } else {
                handleAddonChange(item, 'add');
            }
        } else {
            setLimitError(true);
        }
    };

    /*
    * @description decrement the item from selection list
    * @param {object} item
    */
    const handleDecrement = (item) => {
        if (selectedAddons.length > 0) {
            handleAddonChange(item, 'remove');
        }
    };

    /*
    * @description get the total number of selected items
    * @param {object} item
    */
    const getCounter = (item) => {
        const getQty = selectedAddons?.find((addon) => addon.partNumber === item.partNumber);
        return getQty?.qty || 0;
    };

    return (
        <>
            <div
                className={isMobile ? classes.addonsMobileSection : classes.addonsSection}
                onMouseEnter={() => {
                    if (!isMobile) {
                        hover(buildImgPath(options?.image?.path, options?.image?.name),
                            getShortDescription(options?.shortDescription));
                    }
                }}
            >
                <ListItem key={options} button className={classes.nested}>
                    <ListItemText
                        secondary={(
                            <div className={classes.listItem}>
                                <Grid item sm={2} xs={2}>
                                    <img className={classes.imageWine} src={buildImgPath(options?.image?.path, options?.image?.name)} alt="addOns" />
                                </Grid>
                                <Grid item sm={7} xs={6}>
                                    <Typography
                                        component="span"
                                        variant="body2"
                                        className={isMobile ? classes.addonsMobile : classes.addons}
                                        color="textPrimary"
                                    >
                                        {ReactHtmlParser(options?.name)} <b>${getPrice(options)}</b>
                                    </Typography>
                                </Grid>
                                <Grid item sm={3} xs={4}>
                                    <button
                                        type="button"
                                        id={`${'button-remove-'}${options.legacyId}`}
                                        data-testid={`${'item-remove-'}${options.legacyId}`}
                                        className={classes.button}
                                        onClick={() => {
                                            handleDecrement(options);
                                        }}
                                    >-
                                    </button>
                                    <span>{getCounter(options)}</span>
                                    <button
                                        type="button"
                                        id={`${'button-add-'}${options.legacyId}`}
                                        data-testid={`${'item-add-'}${options.legacyId}`}
                                        className={classes.button}
                                        onClick={() => {
                                            handleIncrement(options);
                                        }}
                                    >+
                                    </button>
                                </Grid>
                            </div>
                        )}
                    />
                </ListItem>
            </div>
        </>
    );
};

AddonItemsList.propTypes = {
    hover: func.isRequired,
    drawer: func,
    options: shape({
        id: string.isRequired,
        shortDescription: string,
        name: string.isRequired,
    }).isRequired,
    classes: object.isRequired,
    handleAddonChange: func.isRequired,
    maxAllowedAddons: number.isRequired,
    selectedAddons: arrayOf(shape({
        name: string,
        partNumber: string,
        id: string,
    })),
    wineValidation: shape({
        wineAvailability: arrayOf({
            isProductAvailable: bool,
        }),
    }),
    setLimitError: func.isRequired,
    isMobile: bool,
};

AddonItemsList.defaultProps = {
    drawer: () => {},
    wineValidation: {},
    selectedAddons: [],
    isMobile: false,
};

export default (withStyles(styles)(AddonItemsList));
